import { useState, type FormEvent } from "react";
import { categories } from "@/mocks/stories";

const FORM_URL = "https://readdy.ai/api/form/dagm09ptto0ceqqnhv80";

interface Parsed {
  code?: string;
  meta?: { message?: string; detail?: string };
  message?: string;
}

export default function SubmitSection() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [descLen, setDescLen] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // anti-spam honeypot: silently pretend success on bot hits
    const honeypot = String(formData.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    setStatus("loading");
    setFormError("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>),
      });
      const text = await res.text();
      let parsed: Parsed = {};
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = { message: text };
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || text;
      const ok =
        res.ok &&
        parsed?.code === "OK" &&
        !/spam/i.test(`${serverMsg}`);

      if (ok) {
        setStatus("success");
        form.reset();
        setDescLen(0);
      } else {
        setStatus("error");
        setFormError(serverMsg ? String(serverMsg) : "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，投稿没有成功发出，请稍后重试。");
    }
  };

  return (
    <section id="submit" className="bg-accent-50/70">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-white">
              <i className="ri-edit-2-line" /> 我要爆料
            </span>
            <h2 className="mt-4 font-heading text-3xl leading-snug text-foreground-900 md:text-4xl">
              你也有让人笑到
              <br className="hidden md:block" />
              捶床的校园故事吗
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-foreground-700 md:text-base">
              宿舍夜话、课堂社死、同桌离奇、群聊翻车……只要够真实够好笑，欢迎匿名投稿。
              被选中的趣事会登上对应专区，投稿作者还能解锁「爆笑之星」徽章。
            </p>

            <ul className="mt-6 space-y-3">
              {[
                { icon: "ri-shield-check-line", t: "严格匿名", d: "化名处理，隐去学校细节" },
                { icon: "ri-check-double-line", t: "轻松上架", d: "审核通过后分分钟进专区" },
                { icon: "ri-emotion-happy-line", t: "友善玩梗", d: "只留笑点，不做网络暴力" },
              ].map((f) => (
                <li key={f.t} className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                    <i className={`${f.icon} text-lg`} />
                  </span>
                  <div>
                    <p className="font-bold text-foreground-900">{f.t}</p>
                    <p className="text-xs text-foreground-600">{f.d}</p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-7 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-background-200 bg-background-50 px-3 py-1.5">
                <i className="ri-mail-line text-primary-600" /> baoxiao@xiaoyuanlaugh.com
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-background-200 bg-background-50 px-3 py-1.5">
                <i className="ri-time-line text-primary-600" /> 工作日 10:00 - 20:00
              </span>
            </div>
          </div>

          <div className="rounded-3xl border border-background-200 bg-background-50 p-5 md:p-7">
            {status === "success" ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 py-10 text-center">
                <span className="text-5xl">🎉</span>
                <h3 className="font-heading text-2xl text-foreground-900">投稿收到啦！</h3>
                <p className="max-w-xs text-sm text-foreground-600">
                  谢谢你的爆料，我们看完后已经在憋笑了。审核通过会尽快上架，注意查收邮箱哦。
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-primary-600"
                >
                  再投一条
                </button>
              </div>
            ) : (
              <form data-readdy-form="校园爆料投稿" onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-bold text-foreground-800">事件分类 *</span>
                    <select
                      name="分类"
                      required
                      className="h-11 rounded-xl border border-background-200 bg-background-50 px-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
                    >
                      <option value="">请选择分类</option>
                      {categories.map((c) => (
                        <option key={c.key} value={c.name}>
                          {c.emoji} {c.name}
                        </option>
                      ))}
                      <option value="其他">其他搞笑日常</option>
                    </select>
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-bold text-foreground-800">你的称呼（昵称）</span>
                    <input
                      name="昵称"
                      type="text"
                      placeholder="怎么称呼你？匿名展示"
                      className="h-11 rounded-xl border border-background-200 bg-background-50 px-3 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </label>
                </div>

                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-bold text-foreground-800">接收邮箱 *</span>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="用于通知上架与领取爆笑之星"
                    className="h-11 rounded-xl border border-background-200 bg-background-50 px-3 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-sm font-bold text-foreground-800">事件标题 *</span>
                  <input
                    name="标题"
                    type="text"
                    required
                    maxLength={40}
                    placeholder="一句话概括你/你同学的离谱操作"
                    className="h-11 rounded-xl border border-background-200 bg-background-50 px-3 text-sm outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="flex items-center justify-between text-sm font-bold text-foreground-800">
                    事件描述 *
                    <span className="font-normal text-foreground-400">{descLen}/500</span>
                  </span>
                  <textarea
                    name="事件描述"
                    required
                    rows={4}
                    maxLength={500}
                    onChange={(e) => setDescLen(e.target.value.length)}
                    placeholder="把来龙去脉写清楚，越有画面感越容易被选中（自动匿名）"
                    className="resize-none rounded-xl border border-background-200 bg-background-50 px-3 py-3 text-sm leading-relaxed outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </label>

                <label className="scanbot-guard" aria-hidden="true">
                  <input
                    type="text"
                    name="contact_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </label>

                {status === "error" && (
                  <p className="rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                    <i className="ri-error-warning-line mr-1.5" />
                    {formError || "提交失败，请检查填写后再试。"}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-primary-500 px-6 text-base font-bold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {status === "loading" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" /> 正在提交
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill" /> 提交爆料
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-foreground-500">
                  提交即表示你同意内容的娱乐化再创作，并接受「友善吃瓜公约」。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}